import React from 'react';
import { connect } from 'react-redux';
import {
  extractSelectedTicketFromState,
  hasMessageBeenRead,
  getNewMessagesEditor,
  extractMlOrdersIdsDataFromState,
  isSelectedTicketReviewTicket,
  getTicketRemovalInProcess,
  isSelectedConversationLoaded,
  getSimplrAccountIdOfSelectedTicket,
  selectTicketLanguage,
  selectIsTicketTranslated,
} from 'extractFromState';
import { selectShowTranslated } from 'selectors/uiMessages';
import {
  initializeMessageScrollListener,
  removeMessageScrollListener,
  scrollRequirementMet,
} from 'actions/scrollActions';
import { setSearchType } from 'actions/tools/ToolActions';
import { findDOMNode } from 'react-dom';
import moment from 'moment';
import DOMPurify from 'dompurify';
import { setAnchorElementsTarget, replaceCouponCodeWithMask } from '../../util';
import Loading, { LoadingWrapper } from 'components/LoadingComponent/LoadingComponent';
import Linkify from 'linkify-html';
import { ERROR } from 'constants/messageStatuses';
import {
  isMessageSmbReply,
  showAsNote,
  getMessagesInConversation,
  getMessageDataToRender,
  hasNewMessage,
  isAttachmentAllowed,
} from 'util/messageUtil';
import { highlightedInlineText } from 'components/common/cssStyles';
import { INBOUND, OUTBOUND } from 'constants/messageDirection';
import * as colors from 'components/common/colorConstants';
import { isLiveChat } from 'util/chat';
import EmailMessage from 'components/Messages/EmailMessage/EmailMessage';
import ChatMessage from 'components/Messages/ChatMessage/ChatMessage';
import get from 'lodash/get';
import head from 'lodash/head';
import { Wrapper, MessageBodyContent, DateHeader } from './Messages.styles';
import isEmpty from 'lodash/isEmpty';
import * as ErrorService from 'services/ErrorService/ErrorService';
import { findAllApiUrlsAndAddSessionToken } from 'util/url';
import { CHAT_BOT_PARTNER_NOTE } from '../../constants/noteTypes';
import environment from 'environment';

const MESSAGE_TIME_DIFF_THRESHOLD = 2;

export class MessagesComponent extends React.Component {
  conversationLoadingChecked = '';

  state = {
    showFeedbackSuccess: false,
    showCopySuccess: false,
    showCopyMsng: false,
    ordersIdsLoaded: false,
    highlightingOrdersOn: true,
  };

  componentDidMount() {
    this.props.initializeMessageScrollListener();
    document.addEventListener('keydown', this.disableHighlightingOrder);
  }

  disableHighlightingOrder = (e) => {
    if (e.altKey && e.ctrlKey && e.keyCode === 72) {
      this.setState({ highlightingOrdersOn: !this.state.highlightingOrdersOn });
    }
  };

  showLoadingSpinner() {
    return (
      this.props.ticketRemovalInProcess ||
      !this.props.ticketAndConversationMatch ||
      (this.props.loading && this.props.loading.isLoading && this.props.loading.component === 'messages')
    );
  }

  componentWillUnmount() {
    this.props.removeMessageScrollListener();
    document.removeEventListener('keydown', this.disableHighlightingOrder);
  }

  componentDidUpdate(previousProps) {
    const { selectedInboxTab } = this.props;

    // controls scroll requirement for short messages - long message logic is in scrollActions.js
    if (!this.props.loading.isLoading && !this.props.messagesViewed) {
      const messagesWrapper = document.getElementById('messages-wrapper');
      const messagesList = document.getElementById('messages-list');

      if (messagesWrapper && messagesList) {
        const containerHeight = messagesWrapper.clientHeight;
        const messageHeight = messagesList.scrollHeight;
        if (containerHeight >= messageHeight) {
          this.props.scrollRequirementMet();
        }
      }
    }

    if (
      (selectedInboxTab === 'assigned' || selectedInboxTab === 'live_chat') &&
      hasNewMessage(previousProps.conversation, this.props.conversation)
    ) {
      let container = findDOMNode(this.refs.oldMessages);
      container.scrollTop = container.scrollHeight;
    }
  }

  processBodyContent(message, content, language) {
    const { mlOrdersIds } = this.props;
    const orderIdsMentionedInTickets = mlOrdersIds && mlOrdersIds.orderIdsMentionedInTickets;
    const { direction } = message;
    let messageContent = content;

    messageContent = findAllApiUrlsAndAddSessionToken(messageContent);

    // highlights every order # found by the ml service
    if (direction === INBOUND && orderIdsMentionedInTickets && this.state.highlightingOrdersOn) {
      orderIdsMentionedInTickets.forEach((orderId) => {
        if (!messageContent) {
          ErrorService.handleError({
            error: Error('variable undefined in getBodyContent'),
            customFields: {
              orderId,
            },
          });

          return messageContent;
        }

        return (messageContent = messageContent.replace(
          new RegExp(orderId, 'ig'),
          `<strong id="orderId" data-value="${orderId}">${orderId}</strong>`
        ));
      });
    }

    if (message.messageBodyHtml) {
      // styleString sets css styles for highlighted orders within the iframe
      // NOTE(pavlo|SPD-16204|2026-09-15): Theme script externalized to a same-origin file so it runs under a
      // strict front-door CSP (script-src 'self', no unsafe-inline). srcDoc inherits the parent origin, so a
      // same-origin src is allowed; the file's own logic (parent data-theme + MutationObserver) is unchanged.
      const publicBase = (environment.publicUrl || '').replace(/\/+$/, '');
      const themeScript = `<script src="${publicBase}/message-theme.js"></script>`;
      const styleString = `<style>${highlightedInlineText[0]}</style>`;
      messageContent = direction === OUTBOUND ? setAnchorElementsTarget(messageContent) : messageContent;

      return (
        <iframe
          title="message"
          srcDoc={styleString + messageContent + themeScript}
          id={`message_${message.messageId}`}
          width="100%"
          // long html does not call onLoad immediately, this ensures content will still be displayed
          // short messages correct this default height instantly with onLoad
          height="2000"
          frameBorder="0"
          onLoad={() => {
            const frame = document.getElementById(`message_${message.messageId}`);
            frame.style.height = 'auto';
            const currentHeight = frame.contentWindow.document.documentElement.scrollHeight;
            frame.style.height = currentHeight ? currentHeight + 10 + 'px' : '500px';
          }}
        />
      );
    }
    if (message.couponCode) {
      messageContent = replaceCouponCodeWithMask(messageContent, message.couponCode);
    } else if (message.decisionTreeCouponCode) {
      messageContent = replaceCouponCodeWithMask(messageContent, message.decisionTreeCouponCode);
    }

    // Do not wrap undefined in HTML
    if ((isEmpty(messageContent) && isAttachmentAllowed(message)) || messageContent === '[No content]') {
      return undefined;
    }

    messageContent = Linkify(DOMPurify.sanitize(messageContent, { WHOLE_DOCUMENT: true }), {
      validate: {
        url: function (value) {
          return /^(http|ftp)s?:\/\//.test(value);
        },
      },
    });
    messageContent = direction === OUTBOUND ? setAnchorElementsTarget(messageContent) : messageContent;

    return (
      <MessageBodyContent
        dir={'auto'}
        lang={language}
        dangerouslySetInnerHTML={{
          __html: messageContent,
        }}
      />
    );
  }

  addExpertNumToMessage(conversation) {
    // Sort messages by date created
    conversation.sort((msg1, msg2) => {
      if (msg1.utcOriginalCreatedAt < msg2.utcOriginalCreatedAt) {
        return -1;
      } else if (msg1.utcOriginalCreatedAt > msg2.utcOriginalCreatedAt) {
        return 1;
      }

      return 0;
    });
    let expertCount = 1;
    // Number experts in order of conversation messages
    let expertsObj = {};
    // Add obfuscated name to each message with expert on it
    conversation.map((msg) => {
      const isMessageFromExpert = msg.source === 'SIMPLR' && !!msg.expert && msg.noteType !== CHAT_BOT_PARTNER_NOTE;
      if (isMessageFromExpert) {
        if (!!msg.expert.simplrExpertId && !msg.expert.isSystemExpert) {
          // Check if expert id is in expertObj
          const simplrExpertId = msg.expert.simplrExpertId;
          if (simplrExpertId in expertsObj) {
            const count = expertsObj[simplrExpertId];
            msg['expert']['obfuscatedName'] = `Expert ${count}`;
          } else {
            expertsObj[simplrExpertId] = expertCount;
            msg['expert']['obfuscatedName'] = `Expert ${expertCount}`;
            expertCount++;
          }
        }
      }

      return msg;
    });

    return conversation;
  }

  // groups messages by day with the day as the key
  getMessagesByDay() {
    return this.addExpertNumToMessage(this.props.conversation).reduce((result, message) => {
      // adding this because moment shows DeprecationWarning in the console:
      // value provided is not in a recognized RFC2822 or ISO format.
      moment.suppressDeprecationWarnings = true;
      const day = moment(message.originalCreatedAt).format('LL').toUpperCase();
      result[day] = result[day] || [];
      result[day].push(message);

      return result;
    }, Object.create({}));
  }

  getFirstMessageOnlyWithDate() {
    const groupedMessages = this.getMessagesByDay();
    const sortedDates = Object.keys(groupedMessages).sort((dateOne, dateTwo) => dateTwo - dateOne);
    const firstMessageDate = head(sortedDates);
    const firstMessageOnly = get(groupedMessages, [firstMessageDate, '0']);

    return { [firstMessageDate]: [firstMessageOnly] };
  }

  checkMessageTimeDiff(time1, time2) {
    let difference = moment(time2).diff(moment(time1), 'minutes');

    return difference >= MESSAGE_TIME_DIFF_THRESHOLD;
  }

  renderMessage(message) {
    const {
      selectedTicket,
      parentTicketId,
      isAdmin,
      simplrAccountId,
      ticketLanguage,
      isTicketTranslated,
      showTranslatedMessages,
    } = this.props;
    const isChat = selectedTicket && isLiveChat(selectedTicket.communicationType);
    const momentFormat = `MM/DD/YY, h:mm:ss${this.props.isDebugModeEnabled ? '.SSS' : ''} a`;
    const messageTime = message.createdAt && moment(message.createdAt).format(momentFormat);
    const messageOriginalTime =
      message.utcOriginalCreatedAt && moment(message.utcOriginalCreatedAt).format(momentFormat);
    const messageTimeDiffExists = this.checkMessageTimeDiff(messageOriginalTime, messageTime);
    const messageStatusColor = message.status === ERROR ? colors.red3 : '#000';
    const { language, content, translatedFrom } = getMessageDataToRender(
      message,
      ticketLanguage,
      isTicketTranslated,
      showTranslatedMessages
    );
    const messageBodyContent = this.processBodyContent(message, content, language);
    const messageClasses = [];
    const commonProps = {
      key: message.messageId,
      message,
      messageClasses,
      messageBodyContent,
      parentTicketId,
      isAdmin,
      messageTime,
      messageOriginalTime,
      messageTimeDiffExists,
      messageStatusColor,
      simplrAccountId,
      language,
      translatedFrom,
    };

    if (isMessageSmbReply(message)) {
      messageClasses.push('smb-message');
    } else {
      if (message.direction !== INBOUND) {
        showAsNote(message) ? messageClasses.push('outbound-note') : messageClasses.push('outbound-message');
      }
    }

    if (isChat) {
      return <ChatMessage {...commonProps} />;
    }

    return <EmailMessage {...commonProps} />;
  }

  renderDaySection(day, messageList, key) {
    const { selectedTicket } = this.props;

    return (
      <React.Fragment key={key}>
        <DateHeader isLiveChat={isLiveChat(selectedTicket && selectedTicket.communicationType)}>{day}</DateHeader>
        {messageList.map((message) => this.renderMessage(message))}
      </React.Fragment>
    );
  }

  render() {
    const messagesByDay = this.getMessagesByDay();

    return (
      <Wrapper name="Messages" id="messages-wrapper" ref="oldMessages">
        {this.showLoadingSpinner() ? (
          <LoadingWrapper>
            <Loading marginTop={'0px'} delay={0} />
          </LoadingWrapper>
        ) : (
          <div id="messages-list" name="messages-list">
            {Object.keys(messagesByDay)
              .sort((dateOne, dateTwo) => dateTwo - dateOne)
              .map((day, key) => this.renderDaySection(day, messagesByDay[day], key))}
          </div>
        )}
      </Wrapper>
    );
  }
}

const mapStateToProps = (state) => {
  let selectedTicket = extractSelectedTicketFromState(state);
  const isReviewTicket = isSelectedTicketReviewTicket(state);

  return {
    isDebugModeEnabled: state.ui.userSettings.debugModeEnabled,
    editorState: getNewMessagesEditor(state),
    selectedTicket: selectedTicket,
    selectedTicketId: selectedTicket && selectedTicket.ticketId,
    conversationId: state.conversation.selectedConversationId,
    conversation: getMessagesInConversation(selectedTicket, state.conversation, state.user, isReviewTicket),
    isAdmin: state.user.isAdmin,
    loading: state.ui.loading,
    selectedInboxTab: state.ui.messages.selectedInboxTab,
    simplrTicketNumber: selectedTicket && selectedTicket.simplrTicketNumber,
    messagesViewed: hasMessageBeenRead(state),
    mlOrdersIds: extractMlOrdersIdsDataFromState(state),
    parentTicketId: state.selectedTicketId,
    ticketRemovalInProcess: getTicketRemovalInProcess(state),
    ticketAndConversationMatch: isSelectedConversationLoaded(state),
    simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
    ticketLanguage: selectTicketLanguage(state),
    isTicketTranslated: selectIsTicketTranslated(state),
    showTranslatedMessages: selectShowTranslated(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    initializeMessageScrollListener() {
      dispatch(initializeMessageScrollListener());
    },
    removeMessageScrollListener() {
      dispatch(removeMessageScrollListener());
    },
    scrollRequirementMet() {
      dispatch(scrollRequirementMet());
    },
    setSearchType(searchType) {
      dispatch(setSearchType(searchType));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MessagesComponent);
